import BaseUploadController from './base_upload_controller'
import NotificationController from './notification_controller'

export default class extends BaseUploadController {
  static targets = ['form']
  
  connect() {
    super.connect() // Call parent connect
    
    // Setup form interceptor with custom handler
    this.setupFormInterceptor((form, fileInputs) => this.processFileUploads(form, fileInputs))
  }
  
  async processFileUploads(form, fileInputs) {
    const filesToUpload = []
    
    // Prepare file list for progress tracking
    for (const input of fileInputs) {
      if (!input.files || input.files.length === 0) continue
      
      // Handle multiple files
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files[i]
        
        // Skip empty entries
        if (!file || file.size === 0) continue
        
        const fileType = this.getFileTypeFromInput(input)
        
        filesToUpload.push({
          id: `${fileType}-${Date.now()}-${i}`,
          name: file.name,
          size: file.size,
          file: file,
          input: input,
          type: fileType,
          stemName: `${this.getFileTypeEmoji(fileType)} ${file.name}`
        })
      }
    }
    
    if (filesToUpload.length === 0) {
      return
    }
    
    // Initialize progress modal
    const progressModal = this.getProgressModalController()
    if (progressModal) {
      progressModal.startUnifiedTracking(filesToUpload)
    }
    
    // Process each file with DirectUpload
    const uploadPromises = filesToUpload.map(fileData => this.uploadFile(fileData, progressModal))
    
    try {
      // Wait for all uploads to complete
      const uploadResults = await Promise.all(uploadPromises)
      
      
      // Create new FormData with the uploaded blob IDs
      const formData = new FormData()
      
      // Add CSRF token
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content
      if (csrfToken) {
        formData.append('authenticity_token', csrfToken)
      }
      
      // Group uploaded files by type
      const filesByType = {}
      uploadResults.forEach((result, index) => {
        const fileData = filesToUpload[index]
        if (result.success && result.blob) {
          const inputName = fileData.input.name.replace('[]', '')
          if (!filesByType[inputName]) {
            filesByType[inputName] = []
          }
          filesByType[inputName].push(result.blob.signed_id)
        }
      })
      
      // Add blob IDs to form data
      Object.entries(filesByType).forEach(([inputName, blobIds]) => {
        // Only technical_sheet is a single file input, all others are multiple
        const isSingleFile = inputName.includes('technical_sheet')
        
        if (isSingleFile && blobIds.length > 0) {
          // For single file inputs, send just the blob ID without array notation
          formData.append(inputName, blobIds[0])
        } else {
          // For multiple file inputs (photos, logos, videos, music_files), send as array
          blobIds.forEach(blobId => {
            formData.append(`${inputName}[]`, blobId)
          })
        }
      })
      
      // Submit the form with blob IDs
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
        },
        credentials: 'same-origin'
      })
      
      if (response.ok) {
        // Handle Turbo Stream or regular response
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('text/vnd.turbo-stream')) {
          const text = await response.text()
          Turbo.renderStreamMessage(text)
        } else {
          // Regular redirect
          window.location.reload()
        }
      } else {
        NotificationController.error('Erreur', 'Erreur lors de l\'envoi du formulaire')
      }
      
    } catch (error) {
      NotificationController.error('Erreur', 'Erreur lors du téléchargement des fichiers')
    }
  }
  
  async uploadFile(fileData, progressModal) {
    try {
      // Use DirectUpload from parent class
      const blob = await this.performDirectUpload(fileData.file, (progress) => {
        // Update progress in modal
        if (progressModal) {
          progressModal.updateProgress(fileData.id, progress, 'upload')
        }
      })
      
      // Mark as complete in modal
      if (progressModal) {
        progressModal.updateProgress(fileData.id, 100, 'upload_complete')
      }
      
      return { success: true, blob: blob }
    } catch (error) {
      // Mark as failed in modal
      if (progressModal) {
        progressModal.markFileFailed(fileData.id)
      }
      
      return { success: false, error: error }
    }
  }
  
  getFileTypeFromInput(input) {
    const name = input.name || input.id || ''
    
    if (name.includes('photo')) return 'photo'
    if (name.includes('logo')) return 'logo'
    if (name.includes('video')) return 'video'
    if (name.includes('music')) return 'audio'
    if (name.includes('technical')) return 'document'
    
    return 'file'
  }
  
  getFileTypeEmoji(type) {
    const emojis = {
      photo: '📸',
      logo: '🎨',
      video: '🎬',
      audio: '🎵',
      document: '📄',
      file: '📁'
    }
    
    return emojis[type] || '📁'
  }
}